import React, { useState, useEffect } from 'react';
import api from './services/api';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import ChatInterface from './components/ChatInterface';
import PropertySelector from './components/PropertySelector';
import ApiHelp from './components/ApiHelp';

export default function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [suggestedQuery, setSuggestedQuery] = useState(null);

  useEffect(() => {
    // Check for token in URL (OAuth callback)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const error = urlParams.get('error');

    if (token) {
      // Save token to localStorage
      api.setToken(token);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Check auth status
      checkAuthStatus();
    } else if (error) {
      setError(`Chyba při přihlašování: ${error}`);
      setLoading(false);
    } else {
      // Normal auth check
      checkAuthStatus();
    }
  }, []);

  useEffect(() => {
    if (authenticated) {
      loadProperties();
    }
  }, [authenticated]);

  const checkAuthStatus = async () => {
    try {
      const status = await api.checkStatus();
      if (status.authenticated) {
        setAuthenticated(true);
        setUser(status.user);
      } else {
        // Clear invalid token
        api.clearToken();
        setAuthenticated(false);
        setUser(null);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      // Clear token on error
      api.clearToken();
      setAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    try {
      const result = await api.getProperties();
      setProperties(result.properties);

      // Auto-select if only one property or restore last selected
      if (result.properties.length === 1) {
        setSelectedProperty(result.properties[0].id);
      }
    } catch (err) {
      setError('Nepodařilo se načíst GA4 properties: ' + err.message);
    }
  };

  const handleLogin = () => {
    api.loginWithGoogle();
  };

  const handleLogout = async () => {
    try {
      await api.logout();
      setAuthenticated(false);
      setUser(null);
      setProperties([]);
      setSelectedProperty(null);
    } catch (err) {
      setError('Odhlášení selhalo: ' + err.message);
    }
  };

  const handleSendQuery = async (query, propertyId, context = []) => {
    return await api.sendQuery(query, propertyId, context);
  };

  const handlePropertyChange = async (newPropertyId) => {
    // Invalidate cache when property changes
    if (selectedProperty && selectedProperty !== newPropertyId) {
      try {
        await api.invalidateCache(selectedProperty);
      } catch (error) {
        console.error('Failed to invalidate cache:', error);
      }
    }
    setSelectedProperty(newPropertyId);
  };

  const handleSuggestedQuery = (query) => {
    setSuggestedQuery(query);
    setSidebarOpen(false);
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background-light dark:bg-background-dark">
        <div className="text-text-light dark:text-text-dark">Načítám...</div>
      </div>
    );
  }

  if (!authenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <Sidebar
        user={user}
        onLogout={handleLogout}
        onQuerySelect={handleSuggestedQuery}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header with Property Selector */}
        <header className="flex-shrink-0 bg-surface-light dark:bg-surface-dark border-b border-border-light dark:border-border-dark">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between h-16 gap-4">
              {/* Logo */}
              <div className="flex items-center gap-2 lg:hidden">
                <img
                  src="http://digitalad.cz/wp-content/uploads/2017/07/digitalad_logo-768x138.png"
                  alt="DigitalAd"
                  className="h-8 w-auto"
                />
              </div>

              {/* Property Selector */}
              <div className="flex-1 max-w-md ml-auto">
                <PropertySelector
                  properties={properties}
                  selectedProperty={selectedProperty}
                  onPropertyChange={handlePropertyChange}
                />
              </div>

              {/* API Help Button */}
              <ApiHelp />

              {/* Mobile Menu Button */}
              <button
                className="p-2 rounded-md lg:hidden flex-shrink-0"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="material-symbols-outlined">menu</span>
              </button>
            </div>
          </div>
        </header>

        {/* Main Chat Area */}
        <main className="flex-1 flex flex-col overflow-hidden">
          <div className="w-full max-w-4xl mx-auto h-full flex flex-col p-4 sm:p-6 lg:p-8">

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
                {error}
              </div>
            )}

            {/* Chat Interface */}
            {selectedProperty ? (
              <ChatInterface
                propertyId={selectedProperty}
                onSendQuery={handleSendQuery}
                suggestedQuery={suggestedQuery}
                onQueryProcessed={() => setSuggestedQuery(null)}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-light dark:text-muted-dark">
                Vyberte GA4 property pro začátek
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
